import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import classNames from "classnames"
import styled from "styled-components"

import { useLazyGetMessageTemplatesQuery, useLazyGetTemplateLibraryGroupsQuery } from "store/api/contractorsApi"
import { useAppDispatch } from "store/hooks"
import { setCurrentTemplateID } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconAdd } from "assets/svg/add-icon.svg"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"

import { convertMessageTemplatesToTableRows, convertTemplatesLibraryToTableRows } from "utils/convertData"
import { DeleteMessageTemplateModal } from "modals/DeleteMessageTemplateModal"
import Table from "components/table/Table"

import { NavLink } from "../NavLink"

import styles from "./index.module.scss"

const LibraryTableContainer = styled.div`
  .tableBody {
    > div {
      border-bottom: none;
    }
  }
`

// TABLE HEADER
const libraryTableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "ID",
    size: "tiny",
    name: "id",
  },
  {
    title: "Title",
    size: "big",
    name: "title",
  },
  {
    title: "Message",
    size: "extra-big",
    name: "message",
    cellComponent: "markdown",
  },
]

const templatesTableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "Title",
    name: "title",
  },
  {
    title: "Creation date",
    size: "small",
    name: "created_at",
  },
  {
    title: "Message",
    size: "extra-big",
    name: "message",
    cellComponent: "markdown",
  },
  {
    title: "ID",
    size: "tiny",
    name: "id",
  },
]

interface MessageTemplatesListProps {
  onCreateTemplate?: (data: UITypes.MessageTemplate) => void
  isLoading?: boolean
}

const MessageTemplatesList = React.memo(({ onCreateTemplate, isLoading }: MessageTemplatesListProps) => {
  const [triggerGetMessageTemplates, { isFetching, data }] = useLazyGetMessageTemplatesQuery()
  const [triggerGetTemplateLibraryGroups, { isFetching: isTemplatesFetching, data: templatesData }] =
    useLazyGetTemplateLibraryGroupsQuery()
  const [showDeleteMessageTemplateModal, setShowDeleteMessageTemplateModal] = useState(false)
  const [convertedTableRows, setConvertedTableRows] = useState<UITypes.TableBodyRow[]>([])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const id = searchParams.get("id")

  const onDeleteClick = (id: number) => {
    dispatch(setCurrentTemplateID(id))
    setShowDeleteMessageTemplateModal(true)
    navigate(`/messages2/create-template`)
  }

  const onAddTemplateLibrary = async (value: number) => {
    const data = templatesData?.data?.flatMap(item => item.items).find(item => item.id === value)

    if (data) {
      onCreateTemplate?.({
        title: data.title,
        message: data.description,
        message_rich: data.description_rich || "",
        frequency: "never" as ApiTypes.Model.MessageTemplateFrequency,
      })
    }
  }

  const onEditClick = (id: number) => {
    navigate(`/messages2/edit-template?id=${id}`, { replace: true })
  }

  const onViewClick = (id: number) => {
    navigate(`/messages2/add-template?id=${id}`, { replace: true })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetMessageTemplates()
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetTemplateLibraryGroups()
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (data?.data) {
      const converted = convertMessageTemplatesToTableRows(data.data)
      setConvertedTableRows(converted)
    } else {
      setConvertedTableRows([])
    }
  }, [data?.data])

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    return {
      className: styles.messageTemplateButtonContainer,
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View template",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: onViewClick,
        },
        {
          icon: <IconAdd />,
          tooltip: "Add to my templates",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: onAddTemplateLibrary,
        },
      ],
    }
  }

  return (
    <>
      <div className={styles.templateList}>
        <div className={styles.tabsWrapper}>
          <NavLink
            className={classNames(styles.tab, {
              [styles.active]: !location.pathname.includes("/messages2/add-template"),
            })}
            to="/messages2/create-template"
          >
            My templates
          </NavLink>
          <NavLink className={styles.tab} to="/messages2/add-template" activeClassName={styles.active}>
            Templates library
          </NavLink>
        </div>
        {location.pathname === "/messages2/add-template" ? (
          <LibraryTableContainer>
            <Table
              headerCells={libraryTableHeaderCells}
              className={styles.table}
              isLoading={isLoading || isTemplatesFetching || isFetching}
              currentItemID={id}
              data={convertTemplatesLibraryToTableRows(templatesData?.data)}
              getRowActions={getRowActions}
              rowsExpanded
            />
          </LibraryTableContainer>
        ) : (
          <Table
            headerCells={templatesTableHeaderCells}
            className={styles.table}
            isLoading={isFetching}
            currentItemID={id}
            data={convertedTableRows}
            onDeleteClick={onDeleteClick}
            onEditClick={onEditClick}
          />
        )}
      </div>
      {showDeleteMessageTemplateModal && (
        <DeleteMessageTemplateModal onCancel={() => setShowDeleteMessageTemplateModal(false)} />
      )}
    </>
  )
})

export default MessageTemplatesList
